import { z } from "zod";

import { providerIntegrationProviderSchema } from "./providerIdentity";

export const providerIngressTelemetryStatusSchema = z.enum([
  "received",
  "accepted",
  "skipped",
  "dropped",
  "claimed",
  "routing_workflow_started",
  "routed",
  "reply_posted",
  "reply_failed",
  "failed_retryable",
  "failed_terminal",
  "echo_suppressed",
]);

export type ProviderIngressTelemetryStatus = z.infer<
  typeof providerIngressTelemetryStatusSchema
>;

export const providerIngressTelemetryTriggerTypeSchema = z.enum([
  "provider_comment",
  "webhook_signature_failure",
]);

export type ProviderIngressTelemetryTriggerType = z.infer<
  typeof providerIngressTelemetryTriggerTypeSchema
>;

// Mirrors samba's VALID_ROUTES exactly (ask_puntt_routing_workflow.py) — stored
// verbatim as passed through from Samba, never re-derived by Christo.
export const routeDecisionSchema = z.enum([
  "existing_chat",
  "existing_project",
  "new_project",
  "chat",
  "discard",
]);

export type RouteDecision = z.infer<typeof routeDecisionSchema>;

// The authoritative post-execution result, computed by Christo from its own
// mutation outcome — not a static lookup off routeDecision. See
// routeOutcome field comment in providerIngressTelemetrySchema for why.
export const routeOutcomeSchema = z.enum([
  "chat_created",
  "chat_reused",
  "project_created",
  "project_reused",
  "discarded",
  "not_applicable",
]);

export type RouteOutcome = z.infer<typeof routeOutcomeSchema>;

// Shared, provider-agnostic drop-reason vocabulary. Provider-specific detail
// (which anchor attribute failed to match, which contact lookup was
// ambiguous, etc.) belongs in providerMetadata, not a new reason string here.
// Mirrored in Python for parity — see the vocabulary parity test.
export const providerIngressTelemetryDropReasonSchema = z.enum([
  "missing_comment_id",
  "missing_bot_identity",
  "ambiguous_bot_identity",
  "authored_by_provider_bot",
  "missing_structured_mention",
  "empty_query",
  "comment_not_found",
  "ambiguous_comment_selection",
  "missing_comment_selection_fields",
  "missing_actor_identifier",
  "actor_user_not_resolved",
  "integration_disabled",
  "workflow_start_failed",
]);

export type ProviderIngressTelemetryDropReason = z.infer<
  typeof providerIngressTelemetryDropReasonSchema
>;

export const deliveryAttemptSchema = z.object({
  receivedAt: z.date(),
  sourceEventType: z.string().optional(),
  outcome: z.string(),
});

export type DeliveryAttempt = z.infer<typeof deliveryAttemptSchema>;

export const providerIngressTelemetryErrorDetailSchema = z.object({
  message: z.string(),
  code: z.string().optional(),
  timestamp: z.date(),
});

export const providerIngressTelemetrySchema = z.object({
  enterpriseId: z.string(),
  provider: providerIntegrationProviderSchema,
  // Optional: absent for Asana signature-failure docs, which are verified
  // before the body is ever parsed — no task id is knowable at that point.
  providerTaskId: z.string().optional(),
  // Omitted entirely when unknown — never set to null. A partial unique
  // index on this field matches an explicit null too, so a null value would
  // still collide across documents. See the service layer's write helpers.
  providerCommentId: z.string().optional(),
  providerEventId: z.string().optional(),
  providerAttachmentId: z.string().optional(),
  providerIngressKey: z.string().optional(),
  providerIngressId: z.string().optional(),
  externalThreadMappingId: z.string().optional(),
  askPunttMessageId: z.string().optional(),
  // Temporal workflow id for the AskPuntt routing workflow this ingress
  // started — the exact value passed to Client.workflow.start, so an
  // operator can jump from this document straight to the workflow in the
  // Temporal UI. Stamped at the same call as status="routing_workflow_started".
  routingWorkflowId: z.string().optional(),
  // "<provider>-<enterpriseId>-<providerTaskId>", matching
  // IntegrationEvent.composite_task_id exactly. Both-or-neither with
  // providerTaskId.
  compositeTaskId: z.string().optional(),
  triggerType: providerIngressTelemetryTriggerTypeSchema,
  sourceEventType: z.string().optional(),
  actorProviderUserId: z.string().optional(),
  resolvedPunttUserId: z.string().optional(),
  botProviderUserId: z.string().optional(),
  linkedChatId: z.string().optional(),
  linkedProjectId: z.string().optional(),
  routeDecision: routeDecisionSchema.optional(),
  // Required with a default so dashboard aggregation never has to
  // special-case a missing value. Set to "not_applicable" at document
  // creation; only Christo's route-endpoint stamp ever moves it off that
  // default, in the same atomic update as routeDecision/status="routed".
  routeOutcome: routeOutcomeSchema,
  outboundCommentId: z.string().optional(),
  status: providerIngressTelemetryStatusSchema,
  dropReason: providerIngressTelemetryDropReasonSchema.optional(),
  errorDetail: providerIngressTelemetryErrorDetailSchema.optional(),
  deliveryAttempts: z.array(deliveryAttemptSchema),
  receivedAt: z.date().optional(),
  claimedAt: z.date().optional(),
  routedAt: z.date().optional(),
  completedAt: z.date().optional(),
  repliedAt: z.date().optional(),
  failedAt: z.date().optional(),
  providerMetadata: z.record(z.unknown()).optional(),
  env: z.enum(["prod", "dev", "staging", "local"]),
  eventContractVersion: z.number(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
});

export type ProviderIngressTelemetry = z.infer<
  typeof providerIngressTelemetrySchema
>;
